// RUSSIAN for S3 (step 2 · catalogue) and S4 (import detail) — Module 02 §4.
//
// Register notes:
//  · The English copy deliberately avoids the word "import" in body prose and
//    says "load" instead. Russian follows the same instinct: «загрузка» for the
//    act and for the job record, never the calque «импортирование».
//  · «Сопоставление столбцов» is the standard Russian for column mapping in
//    every spreadsheet-loading tool a Kazakh office manager has already used.
//  · Row verdicts read as adjectives agreeing with «строка» (feminine), because
//    that is what they actually label: «Без ошибок», «Предупреждение», «Ошибка».
//  · Entity nouns still interpolate ({{product}}, {{facility}}, {{workCenter}},
//    {{bom}}, {{routingStep}}) and arrive in the nominative — the sentences are
//    built so that is grammatical.

import type { Translated } from "../../../i18n-shape.js";
import type { catalogueImportEn } from "./catalogue-import.i18n.js";

export const catalogueImportRu: Translated<typeof catalogueImportEn> = {
  importShared: {
    kinds: {
      products: "Продукты",
      materials: "Материалы",
      boms: "Спецификации",
      routings: "Маршруты",
      customers: "Клиенты",
      suppliers: "Поставщики",
      opening_stock: "Начальные остатки",
      open_sales_orders: "Открытые заказы на продажу",
      open_purchase_orders: "Открытые заказы на закупку",
      machines: "Станки",
      people: "Сотрудники",
    },
    jobStatus: {
      uploaded: "Загружен файл",
      parsing: "Читаем файл",
      mapping: "Нужно сопоставить столбцы",
      validated: "Проверено",
      committing: "Идёт загрузка",
      committed: "Загружено",
      failed: "Не удалось",
      discarded: "Удалено",
    },
    rowStatus: {
      pending: "Не проверена",
      valid: "Без ошибок",
      warning: "Предупреждение",
      error: "Ошибка",
      committed: "Загружена",
      skipped: "Пропущена",
    },
    severity: {
      error: "Ошибка",
      warning: "Предупреждение",
    },
    counters: {
      total: "Строк",
      valid: "Без ошибок",
      warning: "Предупреждений",
      error: "Ошибок",
      committed: "Загружено",
      skipped: "Пропущено",
      notYet: "Станет известно после загрузки файла",
    },
    duplicatePolicy: {
      label: "Если строка уже существует",
      help: "Задаётся при загрузке и не меняется. Повторы внутри одного файла всегда считаются ошибкой.",
      fixed: "Задано при загрузке. Чтобы изменить, удалите эту загрузку и загрузите файл заново.",
      reject: "Отклонить строку",
      rejectHelp: "Строка помечается ошибкой, ничего не перезаписывается.",
      update: "Обновить существующее",
      updateHelp: "Существующая запись заменяется данными из файла.",
      skip: "Пропустить строку",
      skipHelp: "Существующая запись остаётся ровно как есть.",
    },
    fields: {
      sku: "Артикул",
      name: "Название",
      kind: "Вид",
      base_uom: "Базовая единица",
      attribute_schema: "Набор атрибутов",
      tracking: "Способ учёта",
      make_or_buy: "Производство или закупка",
      price_unit_qty: "Цена за количество",
      product_sku: "Артикул — {{product}}",
      version: "Версия",
      component_sku: "Артикул компонента",
      qty: "Количество",
      uom: "Единица",
      line_no: "Номер строки",
      scrap_pct: "% отходов по компоненту",
      consume_at_step_no: "Списывается на операции",
      is_optional: "Необязательная строка",
      facility_id: "{{facility}}",
      output_qty: "Количество выпуска",
      output_uom: "Единица выпуска",
      is_phantom: "Фантомная",
      step_no: "Номер операции",
      work_center: "{{workCenter}}",
      run_minutes: "Минут на операцию",
      setup_minutes: "Минут на наладку",
      run_basis: "База нормирования",
      batch_qty: "Размер серии",
      setup_key: "Группа наладки",
      operation_scrap_pct: "% отходов по операции",
      customer_no: "Номер клиента",
      legal_name: "Юридическое наименование",
      tier: "Ценовая категория",
      display_name: "Рабочее название",
      tax_id: "БИН/ИИН",
      country: "Страна",
      default_currency: "Валюта",
      credit_limit: "Кредитный лимит",
      credit_terms_days: "Отсрочка платежа, дней",
      preferred_channel: "Предпочтительный канал",
      preferred_locale: "Язык общения",
      owner_email: "Эл. почта ответственного",
      contact_name: "Контактное лицо",
      contact_position: "Должность контакта",
      phone: "Телефон",
      email: "Эл. почта",
      tags: "Метки",
      notes: "Заметка",
      billing_line1: "Адрес для счетов, строка 1",
      billing_line2: "Адрес для счетов, строка 2",
      billing_city: "Город для счетов",
      billing_region: "Регион для счетов",
      billing_postal: "Индекс для счетов",
      billing_country: "Страна для счетов",
      order_ref: "Номер заказа в вашем файле",
      unit_price: "Цена за единицу",
      currency: "Валюта",
      promise_date: "Обещанная дата",
      facility_code: "Код: {{facility}}",
      customer_po_ref: "Номер заказа покупателя",
      ordered_on: "Дата заказа",
    },
    fieldUnmapped: "Не импортируется",
    fieldAttribute: "Столбец атрибута",
    fieldCustom: "Название поля",
    fieldCustomHint: "Введите название поля точно так, как его ждёт загрузка.",
    messages: {
      required: "Этот столбец обязателен, а значение пустое.",
      required_missing: "Этот столбец обязателен, а значение пустое.",
      bad_number: "Это значение не является числом.",
      unknown_unit: "Такой единицы нет в вашем списке единиц.",
      unknown_uom: "Такой единицы нет в вашем списке единиц.",
      uom_dimension_mismatch: "Эти две единицы измеряют разные величины и не пересчитываются.",
      invalid_dimension: "Такую размерность единиц мы не знаем.",
      precision_out_of_range: "Число знаков после запятой выходит за допустимые пределы.",
      factor_not_positive: "Коэффициент пересчёта должен быть больше нуля.",
      unknown_reference: "Ссылка ведёт на то, чего пока не существует.",
      unknown_component_sku:
        "{{product}} с таким артикулом не существует, и ни одна строка выше его не создаёт.",
      missing_bom_parent_sku: "Не указан артикул родителя, поэтому строке не к чему относиться.",
      unknown_attribute_schema: "Такого набора атрибутов не существует.",
      unknown_work_center: "{{workCenter}} с таким кодом не существует.",
      unknown_import_kind: "Такой вид загрузки нам неизвестен.",
      invalid_kind: "Это не один из допустимых видов.",
      duplicate_in_file: "Тот же ключ уже встречался выше в этом файле.",
      duplicate_sku_in_file: "Тот же артикул уже встречался выше в этом файле.",
      duplicate_in_db: "Запись с таким ключом уже существует.",
      group_has_errors: "В другой строке той же группы есть ошибка, поэтому вся группа задержана.",
      money_not_integer: "В сумме больше знаков после запятой, чем допускает валюта.",
      currency_mismatch: "Эта валюта не совпадает с валютой вашей компании.",
      length_exceeds_capacity: "Эта длина больше, чем вмещает тара.",
      qty_not_positive: "Количество должно быть больше нуля.",
      location_not_receivable:
        "В это место хранения приходовать нельзя — используйте ячейку, зону приёмки или карантин.",
      measure_uom_invalid: "Мерная тара учитывается в м, кг или шт.",
      value_not_divisible:
        "Эта сумма не делится на количество нацело, поэтому себестоимость единицы нельзя записать точно.",
      bare_container_invalid:
        "В строке пустой тары не может быть ни количества, ни суммы — она регистрируется, а не приходуется.",
      valued_uom_not_base:
        "В строке с суммой должна стоять базовая единица продукта — стоимость хранится за базовую единицу.",
      idempotency_conflict:
        "Место этой строки в журнале занято посторонней записью — обратитесь в поддержку до повторной загрузки.",
      scrap_out_of_range: "Отходы должны быть в пределах от 0 до 100 процентов.",
      run_minutes_invalid: "Минут на операцию должно быть не меньше нуля.",
      setup_minutes_invalid: "Минут на наладку должно быть не меньше нуля.",
      batch_qty_required: "Для нормирования по серии нужен её размер.",
      outsource_code_required: "Для операции на стороне нужен код поставщика.",
      step_order: "Номера операций должны возрастать сверху вниз по файлу.",
      customer_not_found:
        "Клиента с таким номером среди доступных вам нет. Проверьте номер или оставьте ячейку пустой, чтобы создать нового клиента.",
      customer_archived:
        "Этот номер принадлежит клиенту в архиве, поэтому строка не может его обновить.",
      ambiguous_natural_key:
        "Этот БИН/ИИН указан у нескольких ваших клиентов, и строка не может определить, кого обновлять. Добавьте номер клиента.",
      owner_not_member:
        "Среди действующих сотрудников с доступом к клиентам нет человека с такой эл. почтой.",
      owner_not_allowed:
        "Назначать ответственного за клиента может только администратор группы или площадки.",
      permission_denied:
        "Ваша роль не может задать или изменить это значение. Оставьте ячейку пустой или такой, как у клиента сейчас.",
      identity_taken: "Этот телефон или эл. почта уже принадлежит другому контакту.",
      tag_archived: "Метку отправили в архив, пока файл загружался, поэтому она не добавлена.",
      import_importer_not_permitted:
        "Сотрудник, загрузивший этот файл, больше не может добавлять клиентов, поэтому строка не загружена.",
      possible_duplicate:
        "Уже есть клиент с похожим названием или тем же БИН/ИИН. Строка всё равно загрузится — проверьте, что это не та же компания.",
      tag_unknown: "Действующей метки с таким названием нет, поэтому она пропущена.",
      ignored_on_update:
        "Эта строка обновляет клиента, поэтому ячейки контакта и ответственного не записываются.",
      note_redacted: "Заметка в этой строке удалена, потому что заметку с тем же текстом скрыли.",
      invalid_type: "Значение не того вида, который ждёт этот столбец.",
      invalid_literal: "Это не то значение, которое ждёт этот столбец.",
      custom: "Такое значение здесь не принимается.",
      invalid_union: "Значение не подходит ни под одну из форм, которые принимает столбец.",
      invalid_union_discriminator:
        "Значение не подходит ни под одну из форм, которые принимает столбец.",
      invalid_enum_value: "Это не одно из значений, которые принимает столбец.",
      unrecognized_keys: "В строке есть поле, которого нет в карточке клиента.",
      invalid_arguments: "Такое значение здесь не принимается.",
      invalid_return_type: "Такое значение здесь не принимается.",
      invalid_date: "Это не дата.",
      invalid_string: "Текст записан не в той форме, которую ждёт этот столбец.",
      too_small: "Значение слишком короткое или слишком маленькое.",
      too_big: "Значение слишком длинное или слишком большое.",
      invalid_intersection_types: "Такое значение здесь не принимается.",
      not_multiple_of: "Число не кратно шагу, который допускает столбец.",
      not_finite: "Это не конечное число.",
    },
    kindMessages: {
      open_sales_orders: {
        required:
          "Этот столбец обязателен, а ячейка пуста. В строке также нужен номер клиента или ИНН/БИН.",
        customer_not_found:
          "Среди доступных вам клиентов нет клиента с таким номером или ИНН/БИН. Сначала загрузите клиента или исправьте номер.",
        customer_archived:
          "Клиент в архиве. Восстановите его или перенесите заказ на клиента, с которым его объединили.",
        ambiguous_natural_key:
          "Этот ИНН/БИН есть у нескольких ваших клиентов, поэтому непонятно, чей это заказ. Добавьте номер клиента.",
        product_not_found: "{{product}} с таким артикулом не существует.",
        product_archived:
          "{{product}} снят с производства, поэтому заказ на него загрузить нельзя.",
        uom_not_found: "Такой единицы нет в вашем списке единиц.",
        uom_not_convertible:
          "Эту единицу нельзя пересчитать в базовую единицу ({{product}}), поэтому количество не прочитать.",
        facility_not_found: "Нет площадки ({{facility}}) с таким кодом.",
        facility_not_eligible:
          "Отсюда заказ отгрузить нельзя ({{facility}}): площадка неактивна или вы с ней не работаете.",
        facility_unresolved:
          "В заказе не указана площадка ({{facility}}), а ни одна из ваших не может его отгрузить. Добавьте в строку код площадки.",
        ordered_on_in_future: "Дата заказа позже сегодняшней.",
        promise_date_before_ordered_on: "Обещанная дата раньше даты заказа.",
        money_not_integer: "В цене больше знаков после запятой, чем допускает валюта.",
        ambiguous_decimal:
          "Число вроде «1,500» можно прочитать и как полторы, и как тысячу пятьсот. Запишите его как 1500 или 1,5.",
        currency_mismatch: "Это не валюта клиента. Заказ оценивается в валюте клиента.",
        group_header_conflict:
          "Строки этого заказа расходятся в значении, общем для всего заказа: клиенте, площадке ({{facility}}), дате заказа или номере заказа покупателя.",
        order_ref_already_imported:
          "У этого клиента уже загружен заказ с таким номером, и он загрузился бы дважды. Если этот заказ его заменяет, сначала отмените прежний.",
        order_ref_already_imported_named:
          "У этого клиента уже загружен заказ {{order_no}} с таким номером, и он загрузился бы дважды. Если этот заказ его заменяет, сначала отмените заказ {{order_no}}.",
        vat_mode_mixed:
          "Строки берут цены из прайс-листов с разным показом НДС. Загрузите их двумя заказами.",
        permission_denied:
          "Сотрудник, загрузивший файл, не может оформить этот заказ, поэтому он не загружен.",
        import_importer_not_permitted:
          "Сотрудник, загрузивший файл, больше не может оформлять заказы, поэтому строка не загружена.",
        resource_busy:
          "Пока заказ загружался, карточку клиента меняли, поэтому заказ не загружен. Загрузите эти строки ещё раз.",
        order_not_written:
          "Этот заказ не удалось записать, из него ничего не загружено. Загрузите эти строки ещё раз и сообщите в поддержку, если ошибка повторится.",
        group_has_errors: "В другой строке этого заказа есть ошибка, поэтому весь заказ отложен.",
      },
    },
    problems: {
      hasErrorsTitle: "В части строк есть ошибки",
      hasErrorsBody:
        "Исправьте строки и проверьте заново либо включите «загрузить только годные строки», чтобы их пропустить.",
      mappingIncompleteTitle: "Обязательные столбцы не сопоставлены",
      mappingIncompleteBody: "Сопоставьте их перед проверкой файла:",
      kindNotAvailableTitle: "Эта загрузка недоступна",
      kindNotAvailableBody: "Это направление недоступно в этой системе. Ничего не загружено.",
      kindNotAvailableFallback: "модулю, который им владеет",
      fileTooLargeTitle: "Файл слишком большой",
      fileTooLargeBody:
        "Предел — {{sizeMb}} МБ и {{maxRows}} строк. Разделите файл и загружайте части по очереди.",
      badEncodingTitle: "Файл не удалось прочитать",
      badEncodingBody:
        "Это ни UTF-8, ни Windows-1251. Пересохраните файл как .xlsx или как CSV в кодировке UTF-8.",
      emptyFileTitle: "В файле нет строк",
      emptyFileBody: "На первом листе нужна строка заголовков и хотя бы одна строка данных.",
      mergedCellsTitle: "На листе есть объединённые ячейки",
      mergedCellsBody: "Отмените объединение ячеек и загрузите файл заново.",
      financeRequiredTitle: "Для загрузки остатков со стоимостью нужна финансовая роль",
      financeRequiredBody:
        "Эта загрузка проводит суммы в журнал начальных остатков, поэтому выполнить её должен администратор группы или сотрудник с финансовой ролью.",
      stateInvalidTitle: "Этот шаг сейчас неприменим",
      stateInvalidBody:
        "Пока страница была открыта, загрузка ушла дальше. Обновите страницу, чтобы увидеть текущее состояние.",
      notFoundTitle: "Загрузка не найдена",
      notFoundBody: "Она удалена или вам недоступна.",
      facilityRequiredTitle: "Сначала выберите: {{facility}}",
      facilityRequiredBody:
        "Эта загрузка выполняется отдельно для каждой площадки ({{facility}}), поэтому по всей группе её загрузить нельзя.",
      forbiddenTitle: "У вас нет доступа к этой загрузке",
      forbiddenBody:
        "Попросите администратора группы выполнить её или выдать вам соответствующее право.",
      importerNotPermittedTitle: "Этот файл больше нельзя проверить или загрузить",
      importerNotPermittedBody:
        "Загрузивший больше не может добавлять клиентов, ничего не изменено. Отмените и загрузите заново под учётной записью с этим правом.",
      importerNotPermittedOrdersBody:
        "Загрузивший больше не может оформлять заказы, ничего не изменено. Отмените и загрузите заново под учётной записью с этим правом.",
      genericTitle: "Не получилось",
      genericBody: "Запрос выполнить не удалось. Попробуйте ещё раз.",
    },
    redacted: "Скрыто",
    redactedHint: "Денежные столбцы скрыты для вашей роли.",
    redactedNotice:
      "Денежные столбцы в этом файле скрыты для вашей роли. Они всё равно импортируются — вы просто их здесь не видите.",
  },

  // ===========================================================================
  // S3 · Шаг 2 — Каталог
  // ===========================================================================
  catalogueStep: {
    tabs: {
      label: "Как вы хотите загрузить каталог",
      manual: "Вручную",
      import: "Из таблицы",
    },

    manual: {
      title: "Собрать здесь",
      blurb:
        "Создайте продукт ({{product}}) из шаблона, затем добавьте спецификацию ({{bom}}) и операции ({{routingStep}}).",
      note: "Открытие продукта ({{product}}) переводит вас в модуль каталога. Вернуться можно по ленте шагов сверху.",
      openCatalogue: "Открыть каталог целиком",
    },

    importTab: {
      title: "Загрузить таблицу",
      blurb: "Заполните шаблон, перетащите сюда, проверьте и загрузите.",
      kindLabel: "Что вы загружаете?",
      templateTitle: "1 · Возьмите шаблон",
      templateHint:
        "Строка заголовков ровно с теми столбцами, которые понимает эта загрузка. Для продуктов и материалов в неё входят и столбцы атрибутов из вашего отраслевого пакета.",
      download: "Скачать шаблон (.csv)",
      uploadTitle: "2 · Загрузите свой файл",
      notPermitted: "Вы не можете выполнять эту загрузку",
      notPermittedBody:
        "Ваша роль не может выполнить эту загрузку. Попросите администратора группы или площадки выполнить её или дать доступ.",
    },

    dropzone: {
      label: "Перетащите файл сюда",
      hint: "или выберите его — .xlsx либо .csv, до {{sizeMb}} МБ",
      browse: "Выбрать файл",
      chosen: "{{name}} · {{size}}",
      clear: "Убрать",
      accept: ".xlsx, .xlsm или .csv",
    },

    upload: {
      start: "Загрузить и проверить",
      creating: "Готовим загрузку…",
      uploading: "Идёт передача…",
      starting: "Читаем файл…",
      failedTitle: "Передача не завершилась",
      failedBody: "Ничего не загружено. Проверьте связь и попробуйте передать файл ещё раз.",
      retry: "Передать файл ещё раз",
      retryReusesJob:
        "Повтор отправляет тот же файл в ту же загрузку — вторая загрузка не создаётся.",
      progress: "{{percent}}% — {{sent}} из {{total}}",
      cancel: "Отменить передачу",
      offline: "Нет связи. Загрузку можно будет начать, когда связь вернётся.",
    },

    fileProblem: {
      tooLarge:
        "Размер файла — {{size}}. Предел — {{sizeMb}} МБ; разделите его и загрузите частями.",
      unsupported: "Импортировать можно только .xlsx, .xlsm и .csv. Этот файл — {{name}}.",
    },

    jobs: {
      title: "Ваши загрузки",
      filterKind: "Показать",
      filterAny: "Всё",
      loading: "Загружается список…",
      errorTitle: "Не удалось загрузить список",
      errorDetail: "Историю загрузок получить не удалось. Попробуйте ещё раз.",
      emptyTitle: "Загрузок пока нет",
      emptyBody: "Скачайте шаблон, заполните его и перетащите сюда.",
      col: {
        kind: "Что",
        file: "Файл",
        status: "Статус",
        rows: "Строк",
        counts: "Без ошибок / предупреждения / ошибки",
        created: "Когда загружен",
        actions: "Действия",
      },
      open: "Открыть",
      discard: "Удалить",
      discarding: "Удаляется…",
      discardConfirm: "Удалить эту загрузку? Уже загруженное при этом не отменяется.",
      prev: "Назад",
      next: "Вперёд",
      showing: "Показано: {{count}}",
    },

    progress: {
      title: "Готовность каталога",
      blurb: "Проверки перед запуском, на которые влияет этот шаг.",
      neverEvaluated:
        "Они ещё не выполнялись. Запустите проверку, чтобы увидеть состояние каталога.",
      evaluate: "Запустить проверку",
      evaluating: "Идёт проверка…",
      lastEvaluated: "Проверено {{time}}",
      never: "Ещё не проверялось",
      openChecklist: "Открыть полный чек-лист запуска",
      errorTitle: "Не удалось загрузить проверки запуска",
      blocking: "Блокирующая",
      loading: "Загружаются проверки запуска…",
    },

    items: {
      catalogue_products_min: "Есть хотя бы один продукт ({{product}})",
      catalogue_boms_cover_products:
        "У большинства изготавливаемых позиций ({{product}}) есть спецификация ({{bom}})",
      catalogue_routings_cover_products:
        "У большинства изготавливаемых позиций ({{product}}) есть маршрут",
      catalogue_routings_bound:
        "Каждая операция маршрута указывает на реальный рабочий центр ({{workCenter}})",
    },
  },

  // ===========================================================================
  // S4 · Подробности загрузки
  // ===========================================================================
  importDetail: {
    back: "Назад к шагу каталога",
    loading: "Загружаются данные…",
    errorTitle: "Не удалось загрузить эту загрузку",
    errorDetail: "Данные получить не удалось. Попробуйте ещё раз.",
    notPermittedTitle: "Только просмотр",
    notPermittedBody:
      "Вы можете смотреть эту загрузку, но ваша роль не может её менять или выполнять.",

    meta: {
      kind: "Что",
      file: "Файл",
      size: "Размер",
      created: "Когда загружен",
      policy: "Если строка уже существует",
      facility: "{{facility}}",
      none: "—",
      validated: "Проверено {{time}}",
      committed: "Загружено {{time}}",
    },

    parse: {
      title: "Файл передан",
      body: "Сейчас читаем его — для большой таблицы это займёт немного времени.",
      start: "Прочитать файл",
      starting: "Читаем…",
      failedTitle: "Не удалось начать чтение файла",
      failedBody:
        "Ничего не загружено. Попробуйте ещё раз или удалите эту загрузку и передайте файл заново.",
    },

    parsing: {
      title: "Читаем ваш файл",
      body: "Разбираем строку заголовков и каждую строку данных. Страница обновляется сама.",
    },

    mapping: {
      title: "Сопоставьте столбцы",
      blurb: "Распознанные столбцы сопоставлены. Проверьте остальные и запустите проверку.",
      colSource: "Столбец в вашем файле",
      colSample: "Первое значение",
      colTarget: "Попадает в",
      colState: "",
      auto: "Сопоставлено автоматически",
      edited: "Изменено",
      required: "Обязательно",
      save: "Сохранить сопоставление",
      saving: "Сохраняется…",
      saved: "Сопоставление сохранено",
      missingTitle: "Часть обязательных столбцов ещё не сопоставлена",
      missingBody: "Сопоставьте их перед проверкой:",
      duplicateTitle: "Два столбца указывают на одно поле",
      duplicateBody: "В каждое поле может идти только один столбец. Исправьте:",
      validate: "Проверить файл",
      validating: "Идёт проверка…",
      noHeadersTitle: "Сопоставлять нечего",
      noHeadersBody:
        "Файл прочитан, но строки заголовков в нём нет. Удалите загрузку и передайте файл заново.",
      recallBody:
        "Вы уже сопоставляли файл ровно с такими столбцами. Применить то сопоставление, чтобы не делать это заново?",
      recallCta: "Взять моё прошлое сопоставление",
    },

    impact: {
      title: "Что произойдёт при загрузке",
      willLoad: "Строк, которые запишутся в «{{kind}}»: {{count}}.",
      willLoad_one: "Строк, которые запишутся в «{{kind}}»: {{count}}.",
      withWarnings:
        "Из них с предупреждением: {{count}} — они загрузятся, но их стоит потом проверить.",
      withWarnings_one:
        "Из них с предупреждением: {{count}} — она загрузится, но её стоит потом проверить.",
      willSkip: "Строк с ошибками будет пропущено и оставлено в этой загрузке: {{count}}.",
      willSkip_one: "Строка с ошибками будет пропущена и оставлена в этой загрузке: {{count}}.",
      blockedByErrors:
        "Строк с ошибками: {{count}}. Исправьте их и проверьте заново либо отметьте галочку выше, чтобы загрузить остальные без них.",
      blockedByErrors_one:
        "Строка с ошибками: {{count}}. Исправьте её и проверьте заново либо отметьте галочку выше, чтобы загрузить остальные без неё.",
      duplicates: "Уже существующие строки: {{policy}}.",
      irreversible:
        "Загрузку отсюда отменить нельзя. Чтобы её откатить, придётся удалить записи в том модуле, которому они принадлежат.",
    },
    discard: {
      confirmTitle: "Удалить эту загрузку?",
      confirmBody:
        "«{{file}}» и проверенные строки ({{rows}}) будут выброшены. Уже загруженное при этом не отменяется.",
    },

    validated: {
      title: "Результаты проверки",
      allGood: "Все строки прошли проверку. Загружайте, когда будете готовы.",
      someBad:
        "Строк с ошибками: {{errors}} из {{total}}. Исправьте их и проверьте заново либо загрузите только годные строки.",
      revalidate: "Проверить заново",
      editMapping: "Изменить сопоставление столбцов",
      errorReport: "Скачать отчёт об ошибках (.xlsx)",
      commit: "Загрузить в каталог",
      commitCustomers: "Загрузить в базу клиентов",
      commitOrders: "Загрузить заказы в продажи",
      committing: "Идёт загрузка…",
      allowPartial: "Загрузить годные строки, а строки с ошибками пропустить",
      allowPartialHelp:
        "Пропущенные строки остаются в этой загрузке, поэтому файл можно исправить и загрузить их позже.",
      allowPartialHelpGrouped:
        "Заказ, в котором хотя бы одна строка с ошибкой, пропускается целиком, чтобы ни один заказ не загрузился без части строк. Пропущенные заказы остаются в этой загрузке.",
      discard: "Удалить эту загрузку",
      discarding: "Удаляется…",
      discardConfirm: "Удалить эту загрузку? Файл и результаты проверки будут выброшены.",
      fixHint:
        "Строки здесь не редактируются. Неверный столбец исправьте сопоставлением выше, неверное значение — в файле, и загрузите снова.",
    },

    committing: {
      title: "Загружаем ваши строки",
      body: "Каждая строка записывается через модуль, которому она принадлежит. Страница обновляется сама.",
      progress: "{{done}} из {{total}} строк",
    },

    committed: {
      title: "Загружено",
      body: "Загружено строк: {{committed}}.",
      skipped: "Пропущено строк: {{count}}.",
      skipped_one: "Пропущено строк: {{count}}.",
      errors: "Строк с ошибками, которые не загрузились: {{count}}.",
      errors_one: "Строк с ошибками, которые не загрузились: {{count}}.",
      backToStep: "Назад к шагу настройки",
      partialHint:
        "Исправьте пропущенные строки в своей таблице и загрузите её заново как новую загрузку.",
      seeOrders: "Открыть загруженные заказы",
    },

    failed: {
      title: "Эта загрузка не удалась",
      body: "Ничего не загружено. Файл не удалось прочитать целиком.",
      causesTitle: "Обычные причины:",
      causeEncoding: "CSV не в UTF-8 и не в Windows-1251 — пересохраните как .xlsx.",
      causeTooLarge: "Файл больше {{sizeMb}} МБ или {{maxRows}} строк — разделите его.",
      causeMerged: "На листе есть объединённые ячейки — отмените объединение.",
      causeEmpty: "На первом листе есть строка заголовков, но нет строк данных.",
      whatNext: "Исправьте файл и загрузите его заново с шага каталога.",
    },

    discarded: {
      title: "Эта загрузка удалена",
      body: "Ничего не загружено. Чтобы начать заново, передайте файл ещё раз с шага каталога.",
    },

    rows: {
      title: "Строки",
      loading: "Загружаются строки…",
      errorTitle: "Не удалось загрузить строки",
      errorDetail: "Строки получить не удалось. Попробуйте ещё раз.",
      emptyTitle: "Строк нет",
      emptyBody: "Файл прочитан, но строк данных в нём не оказалось.",
      noMatchTitle: "Подходящих строк нет",
      noMatchBody: "Ни одна строка этой загрузки не подходит под фильтр.",
      filterAll: "Все",
      filterValid: "Без ошибок",
      filterWarning: "Предупреждения",
      filterError: "Ошибки",
      filterCommitted: "Загруженные",
      filterSkipped: "Пропущенные",
      searchLabel: "Поиск по строкам",
      searchPlaceholder: "Любое значение в строке…",
      colRow: "Строка",
      colStatus: "Статус",
      colMessages: "Что не так",
      messagesNone: "—",
      prev: "Назад",
      next: "Вперёд",
      showing: "Показано: {{count}}",
      moreColumns: "ещё столбцов: {{count}} — чтобы увидеть все, скачайте отчёт об ошибках",
      moreColumns_one: "ещё столбцов: {{count}} — чтобы увидеть его, скачайте отчёт об ошибках",
      rowLabel: "Строка {{number}}",
    },
  },
};
